import { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, Typography, IconButton, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUserMetadata } from '../../context/UserMetadataContext';
import { useAuth0Tokens } from '../../hooks/useAuth0Tokens';

// OktaModal mirrors GitHub/Entra modal patterns
const OktaModal = ({ isOpen, onClose, onSuccess }) => {
  const { userMetadata } = useUserMetadata();
  const { getTokens } = useAuth0Tokens();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    idpDomain: '',
    tenantId: '',
    apiToken: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

    if (!graphqlEndpoint) {
      setError('GraphQL endpoint configuration is missing');
      setIsLoading(false);
      return;
    }

    try {
      const { apiToken } = await getTokens();

      // Log GraphQL endpoint
      console.log('GraphQL Endpoint:', graphqlEndpoint);

      // Log the company name from metadata
      console.log('Company from metadata:', userMetadata?.company);

      // Validate required data
      if (!formData.idpDomain.trim()) throw new Error('Okta Domain is required');
      if (!formData.tenantId.trim()) throw new Error('Tenant ID is required');
      if (!formData.apiToken.trim()) throw new Error('API Token is required');
      if (!userMetadata?.company) throw new Error('Company information is missing');

      // GraphQL mutation
      const mutation = `
        mutation ConfigureOkta($input: ConfigureOktaInput!) {
          configureOkta(input: $input) {
            success
            message
            integration {
              id
              tenantId
              integrationType
              orgName
              status
              createdAt
              updatedAt
            }
            errors
          }
        }
      `;

      const variables = {
        input: {
          idpDomain: formData.idpDomain.trim(),
          tenantId: formData.tenantId.trim(),
          apiToken: formData.apiToken.trim()
        }
      };

      console.log('GraphQL request:', { mutation, variables: { ...variables, input: { ...variables.input, apiToken: '[REDACTED]' } } });

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      console.log('Response status:', response.status);

      const responseData = await response.json();
      console.log('GraphQL response:', responseData);

      // Check for GraphQL errors
      if (responseData.errors) {
        throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
      }

      // Check mutation response
      const result = responseData.data?.configureOkta;
      if (!result || !result.success) {
        throw new Error(result?.message || result?.errors?.[0] || 'Failed to configure Okta integration');
      }

      // Success!
      onSuccess?.({ status: 'success', message: 'Okta integration successful', ...responseData });
      onClose();
    } catch (err) {
      console.error('Integration error details:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'An error occurred while connecting to Okta');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleClose = useCallback(() => {
    setFormData({ idpDomain: '', tenantId: '', apiToken: '' });
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Connect Okta</Typography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="okta-form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Okta Domain"
            id="idpDomain"
            name="idpDomain"
            value={formData.idpDomain}
            onChange={handleChange}
            placeholder="dev-123456.okta.com"
            required
            helperText="Enter your Okta domain (e.g., dev-123456.okta.com or mycompany.okta.com)"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Tenant ID"
            id="tenantId"
            name="tenantId"
            value={formData.tenantId}
            onChange={handleChange}
            placeholder="Enter your Okta Tenant ID"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="API Token"
            type="password"
            id="apiToken"
            name="apiToken"
            value={formData.apiToken}
            onChange={handleChange}
            placeholder="Enter your Okta API Token"
            required
            helperText="Generate an API token from Security > API > Tokens in your Okta Admin Console"
            sx={{ mb: 2 }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Connecting...' : 'Connect Okta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OktaModal;
